import React, { FC, useContext, useEffect, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { addThousandCommas, isClientSide } from "@utils"

import Button from "@components/Button/Button"
import FakeSelect from "@components/FakeSelect/FakeSelect"
import NumberInterpolator from "@components/NumberInterpolator/NumberInterpolator"
import NumericalInputWithLimit from "@components/NumericalInputWithLimit/NumericalInputWithLimit"

import { CompetitorCalculatorSectionSECTION } from "./CompetitorCalculatorSection.styles"
import ContentfulLpContext from "@context/ContentfulLpContext/ContentfulLpContext"
import { CompetitorCalculatorPlanFragmentFragment, CompetitorCalculatorSectionQueryQuery } from "@graphqlTypes"

const CONTACTS_RATIO = 1.5

const ABSURD_LARGE_NUMBER = 99999

const CompetitorCalculatorSection: FC = () => {
  const { pricingData, competitorData } = useStaticQuery<CompetitorCalculatorSectionQueryQuery>(graphql`
    fragment CompetitorCalculatorPlanFragment on contentfulJsonDataJsonJsonNodePlans {
      name
      baseContacts
      maxContacts
      ctaText
      ctaLink
      id
      prices {
        ...PricingJsonPricesFragment
      }
    }
    query CompetitorCalculatorSectionQuery {
      pricingData: contentfulJsonData(contentful_id: {eq: "45c4tePUA3uIu5WPPfUCez"}) {
        json {
          annualDiscountPercentage
          pricingIncrements
          pricingIncrementsSales
          plans {
            ...CompetitorCalculatorPlanFragment
          }
        }
      }
      competitorData: contentfulJsonData(
        contentful_id: {eq: "6Z4xGbFHziznJ0OiJFl1KB"}
      ) {
        json {
          competitors {
            name
            baseRate
            perUserRate
            planName
            priceDescription
            finePrint
          }
        }
      }
    }
  `)

  if (!(pricingData?.json && competitorData?.json)) return null

  const {
    json: pricingJson
  } = pricingData

  const {
    json: competitorJson
  } = competitorData

  if (!(pricingJson.plans && competitorJson.competitors)) return null

  const { isAnnualDiscountApplied, isSalesPage } = useContext(ContentfulLpContext)
  const [plansToDisplay, setPlansToDisplay] = useState<Array<CompetitorCalculatorPlanFragmentFragment | null>>(pricingJson.plans.slice(1, 3))
  const [currentCompetitor, setCurrentCompetitor] = useState<number>(0)
  const [currentPlan, setCurrentPlan] = useState<number>(0) // 0 = Standard
  const [currentVolume, setCurrentVolume] = useState<number>(500)
  const [currentSize, setCurrentSize] = useState<number>(5)
  const [currentContactsCount, setCurrentContactsCount] = useState<number>(500)
  const [pricingIncrementsToUse, setPricingIncrementsToUse] = useState(pricingJson.pricingIncrements || [])
  const [volumeLimit, setVolumeLimit] = useState<number>(10000)
  const [disabledPlans, setDisabledPlans] = useState<number[]>([])

  const currentPlanData = plansToDisplay[currentPlan]
  const currentCompetitorData = competitorJson.competitors[currentCompetitor]

  if (!currentPlanData || !currentPlanData.prices || !currentCompetitorData) return null

  const hsPriceToDisplay = Math.ceil(
    currentPlanData.prices[`_${currentContactsCount}`] * (
      isAnnualDiscountApplied ? 1 - pricingJson.annualDiscountPercentage / 100 : 1
    )
  )
  const competitorPriceToDisplay = currentCompetitorData.baseRate + currentCompetitorData.perUserRate[currentPlan] * (currentSize || 1)

  useEffect(() => {
    if (!isClientSide() || !isSalesPage) return

    setPlansToDisplay(pricingJson?.plans?.slice(1, 4) || [])
    setVolumeLimit(75000)
    setPricingIncrementsToUse(pricingJson.pricingIncrementsSales || [])
    setCurrentPlan(2)
    setCurrentVolume(1500)
    setCurrentSize(10)
  }, [isSalesPage])

  useEffect(() => {
    if (!isClientSide()) return

    const _currentContactsCount = currentVolume
    const _currentContactsCountRounded = !pricingIncrementsToUse ? 0 : pricingIncrementsToUse.find((v, i) =>
      (v || 0) >= _currentContactsCount &&
      (pricingIncrementsToUse[i - 1] || ABSURD_LARGE_NUMBER) <= _currentContactsCount
    ) || (_currentContactsCount < 50 ? 0 : (pricingIncrementsToUse.slice(-1)[0] || 0))

    const _disabledPlans = []
    // Standard
    if (_currentContactsCountRounded > (plansToDisplay[0]?.maxContacts || ABSURD_LARGE_NUMBER)) {
      _disabledPlans.push(0)
      setCurrentPlan(_currentPlan => _currentPlan < 1 ? 1 : _currentPlan)
    }
    // Plus
    if (_currentContactsCountRounded > (plansToDisplay[1]?.maxContacts || ABSURD_LARGE_NUMBER)) {
      _disabledPlans.push(1)
      setCurrentPlan(_currentPlan => _currentPlan < 2 ? 2 : _currentPlan)
    }

    setDisabledPlans(_disabledPlans)

    setCurrentContactsCount(_currentContactsCountRounded)

  }, [currentPlan, currentVolume, volumeLimit, pricingIncrementsToUse])

  return (
    <CompetitorCalculatorSectionSECTION
      className={isSalesPage ? "is-sales-page" : ""}
      id="CompetitorCalculator"
    >
      <div className="container">
        <div className="grid">
          <div className="column column--2-12-XL">
            <div className="flex calculator">
              <div className="controls">
                <h3>
                  Shopping around?<br />
                  Compare us to
                  {" "}
                  <FakeSelect
                    className="competitor-select"
                    iconSize="28px"
                    options={
                      competitorJson.competitors.map((competitor, i) => {
                        if (!competitor) return
                        return {
                          name: competitor.name || "",
                          value: i,
                        }
                      }).filter(c => !!c)
                    }
                    onChange={e => setCurrentCompetitor(Number(e.target.value))}
                    value={currentCompetitor}
                  />
                </h3>
                <div className="form">
                  <label className="full-width">
                    <span>Monthly contact volume*</span>
                    <NumericalInputWithLimit
                      limit={volumeLimit}
                      name="contactsCount"
                      onChange={setCurrentVolume}
                      showCommas
                      value={currentVolume}
                    />
                    <p className="type--text-S margin-top--base-0-75 margin-bottom--0 color--text-headline">
                      About <strong>{addThousandCommas(Math.floor(currentVolume * CONTACTS_RATIO))}</strong> <em>emails</em> or <em>tickets</em> per month for most customers
                    </p>
                  </label>
                  <label className="full-width">
                    <span>Support team size*</span>
                    <NumericalInputWithLimit
                      limit={250}
                      name="currentSize"
                      onChange={setCurrentSize}
                      showCommas
                      value={currentSize}
                    />
                  </label>
                  <span className="full-width details">Estimate only, please check competitor site for full details.</span>
                </div>
              </div>
              <div className="comparison">
                <div className={`plan--hs plan--${currentPlanData?.id || ""}`}>
                  <div className="gradient-background" />
                  <div className="content">
                    <div>
                      <FakeSelect
                        className="plan-select type--headline-medium-M"
                        options={
                          plansToDisplay.map((plan, i) => !plan ? null : ({
                            disabled: disabledPlans.includes(i),
                            name: `Help Scout ${plan.name}`,
                            value: i,
                          })).filter(p => !!p)
                        }
                        onChange={e => setCurrentPlan(Number(e.target.value))}
                        value={currentPlan}
                      />
                    </div>
                    <div className="plan-price">
                      <span
                        className="plan-price--value type--headline-medium-5XL font-weight--medium color--text-headline"
                      >
                        $
                        <NumberInterpolator
                          useRoundToThousandsWithDecimal
                          value={hsPriceToDisplay}
                        />
                      </span>
                      <span className="plan-price--mo type--text-XS color--text-default">/month</span>
                    </div>
                    <p className="plan-description type--text-S color--text-headline margin-bottom--0">
                      <strong>
                        {
                          currentContactsCount > (currentPlanData.maxContacts || 50000) ?
                            `Up to ${addThousandCommas(currentPlanData.maxContacts || 0)} contacts` :
                            currentContactsCount < (currentPlanData.baseContacts || 0) ?
                              `Starts with ${addThousandCommas((currentPlanData.baseContacts || 0))} contacts` :
                              `With ${addThousandCommas(currentContactsCount || 0)} contacts`
                        }
                      </strong>
                      <br />
                      and unlimited users
                    </p>
                    {!isSalesPage && (
                      <Button href={currentPlanData.ctaLink || "/"}>{currentPlanData.ctaText || ""}</Button>
                    )}
                  </div>
                </div>
                <div className="plan--competitor">
                  <div className="content">
                    <div className="type--headline-medium-M color--text-light">
                      {currentCompetitorData.name} {currentCompetitorData.planName[currentPlan]}
                    </div>
                    <div className="plan-price">
                      <span
                        className="plan-price--value type--headline-medium-5XL font-weight--medium color--text-headline"
                      >
                        $
                        <NumberInterpolator
                          useRoundToThousandsWithDecimal
                          value={competitorPriceToDisplay}
                        />
                      </span>
                      <span className="plan-price--mo type--text-XS color--text-default">/month</span>
                    </div>
                    <p className="type--text-XS">{currentCompetitorData.priceDescription[currentPlan]}</p>
                    <p
                      className="fine-print type--text-XS color--text-light"
                      dangerouslySetInnerHTML={{ __html: currentCompetitorData.finePrint || "&nbsp;" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CompetitorCalculatorSectionSECTION>
  )
}

export default CompetitorCalculatorSection
